/* eslint-disable max-lines */
import { schema } from 'normalizr';
import paginateAction from '@utils/paginateAction';
import Template from '@models/CourseTemplate';
import algo from '@utils/algos';
import { HOST_TYPE } from '@utils/api-helper/host';
import actionTypes from '@educator/Composite/DashBoard/DynamicBanners/constants';
import { programmeTypes } from '@constants/index';
import {
  getIsCbtTestSeries,
  getIsOfflineTestSeries
} from '@educator/Container/InputFields/utils';
import { educatorTypes } from '../constants';
import { paginatorListSchema, userSchema } from '../schemas';

const getEducatorLeaderBoard = (goalUid, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_EDUCATOR_LEADERBOARD,
  endpoint: next,
  method: 'get',
  key: goalUid,
  resetWithoutEmpty,
  schema: paginatorListSchema
});

export const fetchEducatorLeaderboard = (
  goalUid,
  nextPage,
  resetWithoutEmpty = false,
  params = ''
) =>
  paginateAction(
    `v1/gamification/educator/leaderboard/?goal_uid=${goalUid}&limit=25${
      params ? `&${params}` : ''
    }`,
    getEducatorLeaderBoard(goalUid, resetWithoutEmpty),
    'educatorLeaderboard',
    goalUid,
    resetWithoutEmpty
  )(nextPage);

const getCourseFeedback = (collectionUID, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_COURSE_FEEDBACK,
  endpoint: next,
  method: 'get',
  key: collectionUID,
  resetWithoutEmpty,
  schema: paginatorListSchema
});

export const fetchCourseFeedback = (
  isTestSeries,
  collectionUID,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/feedback/${
      isTestSeries ? 'test_series' : 'plus_course'
    }/${collectionUID}/`,
    getCourseFeedback(collectionUID, resetWithoutEmpty),
    'courseFeedback',
    collectionUID,
    resetWithoutEmpty
  )(nextPage);

export const fetchProfileInfo = (username, goalUid = '') => {
  const params = goalUid ? `?goal_uid=${goalUid}` : '';
  return {
    type: educatorTypes.FETCH_PROFILE_INFO,
    endpoint: `v1/user/${username}/info/${params}`,
    method: 'get',
    username,
    schema: new schema.Object({ user: userSchema }),
    entityType: 'user'
  };
};

export const fetchProfileStats = (username) => ({
  type: educatorTypes.FETCH_PROFILE_STATS,
  endpoint: `v1/user/${username}/stats/`,
  method: 'get',
  username
});

const getProfileFeed = (username, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_PROFILE_FEED,
  endpoint: next,
  method: 'get',
  key: username,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'feedrow',
  processData: (data) => {
    const newData = data;
    newData.results = newData.results.map((feed) => ({
      ...feed,
      key: username,
      data: feed.data.map((course) => {
        const tmpCourse = { ...course, ...course.programme };
        delete tmpCourse.author;
        return tmpCourse;
      })
    }));
    return newData;
  }
});

export const fetchProfileFeed = (
  username,
  goalUid,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/search_v3/educators/${username}/plus/extended/v2/?goal_uid=${goalUid}'`,
    getProfileFeed(username, resetWithoutEmpty),
    'profileFeed',
    username,
    resetWithoutEmpty
  )(nextPage);

const getCoursesWithFilters =
  (userName, resetWithoutEmpty, key, filterKey, entityType) => (next) => ({
    type: educatorTypes.FETCH_FILTERED_COURSES,
    endpoint: next,
    method: 'get',
    key,
    filterKey,
    resetWithoutEmpty,
    schema: paginatorListSchema,
    directPaginatedEntity: true,
    entityType,
    processData: (data) => {
      const newData = data;
      newData.results = data.results.map((resultItem) => ({
        ...resultItem,
        ...resultItem.programme
      }));
      return newData;
    }
  });

export const fetchCoursesWithFilters = (
  userName,
  filters,
  sortsApplied,
  nextPage,
  resetWithoutEmpty = false,
  entityTypeStr
) => {
  let entityType = entityTypeStr;
  if (entityType !== 'batch' && entityType !== 'course') {
    entityType = 'course';
  }
  let defaultURL = `v1/search_v3/educators/${userName}/courses/v2/?limit=12&filters_applied=${encodeURIComponent(
    JSON.stringify(filters)
  )}`;
  if (sortsApplied)
    defaultURL += `&sorts_applied=${encodeURIComponent(
      JSON.stringify(sortsApplied)
    )}`;
  const key = `${userName}_${filters.goal.id}_${filters.filter_by.id}_${
    filters.show_only.id
  }_${filters.language.id}_${filters.topic.id}_${
    sortsApplied?.free.id || 'relevance'
  }`;
  const filterKey = `${userName}_${filters.goal.id}_${filters.filter_by.id}_${filters.show_only.id}_${filters.language.id}_${filters.topic.id}`;
  return paginateAction(
    defaultURL,
    getCoursesWithFilters(
      userName,
      resetWithoutEmpty,
      key,
      filterKey,
      entityType
    ),
    'filteredCourses',
    key,
    resetWithoutEmpty
  )(nextPage);
};

const getEducatorTestimonials = (key, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_EDUCATOR_TESTIMONIALS,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  processData: (data) => {
    const newData = data;
    newData.results = newData.results.map((resultItem) => {
      const { commenter, ...testimonialInfo } = resultItem;
      return {
        ...testimonialInfo,
        user: commenter
      };
    });
    return newData;
  }
});

export const fetchEducatorTestimonials = (
  username,
  milestoneId,
  nextPage,
  resetWithoutEmpty = false
) => {
  const key = `${username}_${milestoneId}`;
  return paginateAction(
    `v1/gamification/educator/hat/testimonial/?username=${username}&milestone_registry_id=${milestoneId}`,
    getEducatorTestimonials(key, resetWithoutEmpty),
    'educatorTestimonials',
    key,
    resetWithoutEmpty
  )(nextPage);
};

export const toggleEducatorFollowingStatus = (username, follow) => ({
  type: educatorTypes.TOGGLE_FOLLOWING_STATUS,
  endpoint: `v1/user/${username}/${
    follow ? 'follow' : 'unfollow'
  }/?is_community_ass_enabled=true`,
  method: 'post',
  username,
  follow
});

const getLearnerCourses = (key, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_LEARNER_COURSES,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  entityType: 'course',
  directPaginatedEntity: true
});

export const fetchLearnerCourses = (
  username,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/user/${username}/completed_courses_v2/`,
    getLearnerCourses(username, resetWithoutEmpty),
    'learnerCourses',
    username,
    resetWithoutEmpty
  )(nextPage);

export const fetchLearnerMilestones = (username) => ({
  type: educatorTypes.FETCH_LEARNER_MILESTONES,
  endpoint: `v1/gamification/learner/milestones/details/?username=${username}`,
  method: 'get',
  username
});

const getLearnerFollowing = (learnerUsername, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_LEARNER_FOLLOWING_LIST,
  endpoint: next,
  method: 'get',
  key: learnerUsername,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  entityType: 'user',
  directPaginatedEntity: true
});

export const fetchLearnerFollowing = (
  learnerUsername,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    `v1/user/${learnerUsername}/follows/?limit=5`,
    getLearnerFollowing(learnerUsername, resetWithoutEmpty),
    'learnerFollowing',
    learnerUsername,
    resetWithoutEmpty
  )(nextPage);

export const writeDedication = (
  dedicationText,
  educatorUsername,
  milestoneId
) => ({
  type: educatorTypes.WRITE_DEDICATION,
  endpoint: 'v1/gamification/user/milestones/dedicate/',
  method: 'post',
  data: {
    learner_milestone_registry_id: milestoneId,
    text: dedicationText,
    educator_username: educatorUsername
  }
});

const getEducatorDashboardCourses = (key, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_EDUCATOR_DASHBOARD_COURSES,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'course'
});

export const fetchEducatorDashboardCourses = (
  courseType,
  courseState = null,
  nextPage,
  resetWithoutEmpty = false
) => {
  const key = `${courseType}${courseState ? `-${courseState}` : ''}`;
  let url = `v1/uplus/mine/programmes/?limit=12&prog_type=${
    courseType === 'special-classes' ? 2 : 1
  }`;
  if (courseType === 'free-courses') url = 'v1/collections/mine/?limit=12';
  if (courseState && courseType !== 'free-courses')
    url += `&state=${courseState}`;
  return paginateAction(
    url,
    getEducatorDashboardCourses(key, resetWithoutEmpty),
    'educatorDashboardCourses',
    key,
    resetWithoutEmpty
  )(nextPage);
};

const getEductatorScheduledClasses = (key, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_EDUCATOR_SCHEDULED_CLASSES,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'item',
  processData: (data) => {
    const newData = data;
    newData.results = newData.results.map((item) => {
      const newItem = item;
      const value =
        newItem.type === 'post' ? newItem.value.live_class : newItem.value;
      const { programme, uid } = newItem.collection;
      delete newItem.collection;
      return {
        ...newItem,
        ...value,
        collection2: { ...programme, uid }
      };
    });
    return newData;
  }
});

export const fetchEductatorScheduledClasses = (
  startEpoch,
  nextPage,
  resetWithoutEmpty = false
) => {
  const key = 'scheduledClasses';
  return paginateAction(
    `v1/users/educator/schedule/?start=${startEpoch / 1000}`,
    getEductatorScheduledClasses(key, resetWithoutEmpty),
    'eduScheduledClasses',
    key,
    resetWithoutEmpty
  )(nextPage);
};

export const enableCourseTaPoweredState = () => {
  return {
    type: educatorTypes.ENABLE_TA_POWERED
  };
};

export const fetchAllLanguages = () => ({
  type: educatorTypes.FETCH_ALL_LANGUAGES,
  endpoint: 'v1/constants/all_languages/',
  method: 'get',
  schema: new schema.Array(),
  key: 'allLanguages',
  processData: (data) => {
    const newData = data.filter((language) => language.language_code !== 0);
    return newData.map((language) => ({
      ...language,
      id: language.language_code,
      code: language.language_code,
      display_name: language.name
    }));
  }
});

export const searchContentSlideLanguage = () => ({
  type: educatorTypes.FETCH_CONTENT_SLIDE_LANGUAGES,
  endpoint: 'v1/constants/all_languages/',
  method: 'get',
  schema: new schema.Array(),
  processData: (data) => {
    const newData = data.filter((language) => language.language_code !== 0);
    return newData.map((language) => ({
      ...language,
      id: language.language_code,
      code: language.language_code
    }));
  }
});

export const updateCourseTextField = (value, id) => ({
  type: educatorTypes.UPDATE_COURSE_DETAILS,
  value,
  id
});

export const updateCheckBox = (id, value) => ({
  type: educatorTypes.UPDATE_CHECK_BOX,
  value,
  id
});

export const updateCourseDropdownFields = (value, id) => ({
  type: educatorTypes.UPDATE_COURSE_DROPDOWN_FIELDS,
  value,
  id,
  isSingleValue: true
});

export const updateSessionDropdownFields = (value, id) => {
  return {
    type: educatorTypes.UPDATE_SESSION_DROPDOWN_FIELDS,
    value,
    id,
    isSingleValue: true
  };
};

export const removeAutoCompleteTag = (value, id, key) => ({
  type: educatorTypes.REMOVE_TAG,
  value,
  id,
  key
});

export const removeConcept = (value, key = 'selectedConcepts') => ({
  type: educatorTypes.REMOVE_CONCEPT,
  value,
  key
});

export const updateCourseAutoCompleteFields = (value, id, isSingleValue) => ({
  type: educatorTypes.UPDATE_COURSE_AUTOCOMPLETE_FIELDS,
  value,
  id,
  isSingleValue
});

export const updateCourseDetailsData = (value = {}) => ({
  type: educatorTypes.UPDATE_COURSE_DETAILS_DATA,
  value
});

export const searchGoal = (query) => ({
  type: educatorTypes.SEARCH_GOAL,
  endpoint: `v1/topology/goal/search_goals/?q=${encodeURIComponent(query)}`,
  method: 'get'
});

export const searchTopology = (query, level, parentUID) => ({
  type: educatorTypes.SEARCH_TOPIC,
  endpoint: `v1/topology/moderators/search_topology/?level=${level}&q=${query}&max_parent_level=10&parent_uid=${parentUID}`,
  method: 'get'
});

export const searchExam = (query, goalUID) => ({
  type: educatorTypes.SEARCH_EXAM,
  endpoint: `v1/exams/search?name=${query}&goal_uid=${goalUID}`,
  method: 'get'
});

export const searchEducator = (query) => ({
  type: educatorTypes.SEARCH_EDUCATOR,
  endpoint: `v1/search_v3/search/educators/?should_get_systemusers_also=true&limit=30&q=${query}`,
  method: 'get',
  schema: new schema.Array(),
  processData: (data) => {
    const newResult = data.results.map(({ value }) => ({
      username: value.username,
      id: value.username,
      uid: value.uid,
      name: `${value.first_name} ${value.last_name}`,
      avatar: value.avatar
    }));
    return newResult;
  }
});

export const addPlusCourse = (data) => ({
  type: educatorTypes.CREATE_PLUS_COURSE,
  endpoint: 'v1/uplus/moderator/add/programme/',
  method: 'post',
  data
});

export const editPlusCourse = (data) => ({
  type: educatorTypes.EDIT_PLUS_COURSE,
  endpoint: 'v1/uplus/moderator/edit/programme/',
  method: 'post',
  data
});

export const addTestSeriesCourse = (data) => ({
  type: educatorTypes.CREATE_TEST_SERIES_COURSE,
  endpoint: 'v1/uplus/moderator/add/programme/',
  method: 'post',
  data
});

export const editTestSeriesCourse = (data) => ({
  type: educatorTypes.EDIT_TEST_SERIES_COURSE,
  endpoint: 'v1/uplus/moderator/edit/programme/',
  method: 'post',
  data
});

export const createSpecialClass = (data) => ({
  type: educatorTypes.CREATE_SPECIAL_CLASS,
  endpoint: 'v1/uplus/moderator/create_special_class/programme/',
  data,
  method: 'post'
});

export const editSpecialClass = (data) => ({
  type: educatorTypes.EDIT_SPECIAL_CLASS,
  endpoint: 'v1/uplus/moderator/update_special_class/programme/',
  data,
  method: 'post'
});

export const getYoutubeDetails = (classUid) => ({
  type: educatorTypes.GET_YOUTUBE_DETAILS,
  endpoint: `v1/uplus/special-class-youtube-stream/?class_uid=${classUid}`,
  method: 'get'
});

export const sendYoutubeDetails = (data) => ({
  type: educatorTypes.SEND_YOUTUBE_DETAILS,
  endpoint: 'v1/uplus/special-class-youtube-stream/',
  data,
  method: 'post'
});

export const uploadThumbnail = (thumbnail_image, courseUID) => ({
  type: educatorTypes.UPLOAD_THUMBNAIL,
  endpoint: `v1/collections/thumbnail/${courseUID}/`,
  method: 'put',
  data: { thumbnail_image }
});

export const removeBrochure = (courseUID) => ({
  type: educatorTypes.REMOVE_BROCHURE,
  endpoint: 'v1/uplus/moderator/remove_brochure/programme/',
  method: 'post',
  data: { collection_uid: courseUID }
});

export const fetchCourseTypes = () => ({
  type: educatorTypes.FETCH_COURSE_TYPES,
  endpoint: 'v1/collections/course-types/',
  method: 'get',
  schema: new schema.Array(),
  key: 'courseTypes',
  processData: (data) =>
    Object.keys(data)
      .filter((courseKey) => courseKey !== '0')
      .map((value) => ({
        value: parseInt(value, 10),
        label: data[value]
      }))
});

export const setCourseType = (result) => ({
  type: educatorTypes.SET_COURSE_TYPE,
  result,
  key: 'courseType'
});

export const fetchCourseDetails = (courseUID) => ({
  type: educatorTypes.FETCH_COURSE_DETAILS,
  endpoint: `v1/uplus/moderator/programme/${courseUID}/`,
  method: 'get'
});

export const fetchRecordedVideoDetails = ({ courseUID, videoUID }) => ({
  type: educatorTypes.FETCH_RECORDED_VIDEO_DETAILS,
  endpoint: `v1/uplus/moderator/programme/${courseUID}/video_preview?video_uid=${videoUID}`,
  method: 'get'
});

export const fetchBannersInfo = () => ({
  type: actionTypes.FETCH_BANNERS_INFO,
  method: 'get',
  endpoint: `api/policies/${
    process.env.ENVIRONMENT === 'gamma'
      ? 'educatorBannersGammaWeb'
      : 'educatorBannersProdWeb'
  }`,
  hostType: HOST_TYPE.CMS
});

export const updateSessionDetails = (value, id) => ({
  type: educatorTypes.UPDATE_SESSION_VALUE,
  value,
  id
});

export const addConcept = (value, id = 'selectedConcepts') => ({
  type: educatorTypes.UPDATE_SESSION_CONCEPTS,
  value,
  id
});

export const fetchCoAuthors = (courseUID) => ({
  type: educatorTypes.FETCH_CO_AUTHOR,
  endpoint: `v1/uplus/programmes/${courseUID}/co_authors/`,
  method: 'get',
  schema: new schema.Array(),
  key: 'coAuthors',
  processData: (data) =>
    data.map((author) => ({
      ...author,
      name: `${author.first_name} ${author.last_name}`
    }))
});

export const addItem = (data) => {
  return {
    type: educatorTypes.ADD_ITEM,
    endpoint: 'v2/uplus/moderator/item/add/',
    method: 'post',
    data
  };
};

export const deleteItem = (session, collection_uid) => ({
  type: educatorTypes.DELETE_ITEM,
  endpoint: 'v1/uplus/moderator/item/delete/',
  method: 'post',
  session,
  data: {
    item_type: session.type,
    item_uid: session.uid,
    rank: session.rank,
    collection_uid
  }
});

export const setSessionEducator = (value, id) => ({
  type: educatorTypes.SET_SESSION_EDUCATOR,
  value,
  id
});

export const setTAEducatorForSpecialClass = (value) => ({
  type: educatorTypes.SET_TA_EDUCATOR_SC,
  value
});

export const setUpEditSession = (value) => {
  return {
    type: educatorTypes.SETUP_EDIT_SESSION,
    value
  };
};

const getKeysForEditITem = (session) => {
  if (getIsCbtTestSeries(session.programmeType)) {
    return {
      is_offline_quiz: Boolean(session.isOfflineQuiz)
    };
  }
  let extraKeys = { is_offline_session: false };
  if (session.programmeType === programmeTypes.HYBRID_COURSE) {
    if (session?.room?.uid) {
      extraKeys = {
        is_offline_session: true,
        room_uid: session?.room?.uid
      };
    } else {
      extraKeys = {
        is_offline_session: false,
        room_uid: null
      };
    }
  }
  if (getIsOfflineTestSeries(session.programmeType)) {
    if (session?.isOfflineTest) {
      extraKeys = {
        is_offline_session: true,
        venue: session?.venue
      };
    } else {
      extraKeys = {
        is_offline_session: false,
        venue: ''
      };
    }
  }
  return extraKeys;
};

export const editItem = (session, collection_uid, isTAPowered = true) => {
  const extraKeys = {};
  if (isTAPowered) {
    extraKeys.ta_user_uid = session.selectedTA?.uid;
  }
  return {
    type: educatorTypes.EDIT_SESSION,
    endpoint: 'v1/uplus/moderator/item/edit/',
    method: 'post',
    data: {
      author_username: session.selectedEducator.username,
      instructions: session?.instructions,
      co_author_username: session?.selectedCoAuthor?.username,
      collection_uid,
      duration: session.duration,
      item_uid: session.uid,
      live_at: session.liveAt,
      start_time: session.liveAt,
      title: session.title,
      type: session.type,
      topology_uids: session.selectedConceptUIDs,
      ...extraKeys,
      ...getKeysForEditITem(session)
    }
  };
};

export const reorderItem = (collection_uid, rank, new_rank) => ({
  type: educatorTypes.REORDER_COURSE_SESSIONS,
  endpoint: 'v1/uplus/moderator/item/update/',
  method: 'post',
  data: {
    collection_uid,
    rank,
    new_rank
  },
  rank,
  new_rank
});

export const autoScheduleCourse = (
  collection_uid,
  schedule,
  ISODate,
  roomUId
) => ({
  type: educatorTypes.AUTO_SCHEDULE_COURSE_SESSIONS,
  endpoint: 'v1/uplus/moderator/item/auto-schedule/',
  method: 'post',
  data: {
    collection_uid,
    classes_schedule: schedule,
    room_uid: roomUId
  },
  schedule,
  ISODate
});

export const stateChange = (collection_uid, state) => ({
  type: educatorTypes.STATE_CHANGE,
  endpoint: 'v1/uplus/moderator/state_change/programme/',
  method: 'post',
  data: {
    collection_uid,
    state,
    show_all_clashes: true
  }
});

export const setCreateEditSessionError = (result) => ({
  type: educatorTypes.SET_CREATE_EDIT_SESSION_ERROR_FLAG,
  result,
  key: 'createEditSessionError'
});

export const clearAddItemForm = () => ({
  type: educatorTypes.CLEAR_ADD_ITEM_FORM
});

export const clearCourseFields = () => ({
  type: educatorTypes.CLEAR_COURSE_FIELDS
});

export const fetchEducatorLiveClassURL = (classUID) => ({
  type: educatorTypes.FETCH_EDU_LIVE_CLASS_URL,
  method: 'get',
  endpoint: `v1/uplus/educator_next_class/${
    classUID ? `?uid=${classUID}` : ''
  }`,
  key: 'liveClassData'
});

export const fetchTALiveClassURL = () => ({
  type: educatorTypes.FETCH_TA_LIVE_CLASS_URL,
  method: 'get',
  endpoint: 'v1/uplus/teaching_assistant_next_class/',
  key: 'liveClassData'
});

// Post publish course page apis

export const fetchCourseStats = (collectionUId) => ({
  type: educatorTypes.FETCH_COURSE_STATS,
  endpoint: `v1/uplus/programmes/${collectionUId}/course_statistics/`,
  method: 'get',
  schema: new schema.Object({}),
  processData: (data) => ({
    todayHours: data.today_taught_hours / (60 * 60),
    totalHours: data.total_classes_duration / (60 * 60),
    currentHours: data.total_hours_taught / (60 * 60),
    totalWatchMins: data.watch_minutes,
    courseRating: data.rating.avg,
    noOfIndividuals: data.rating.count,
    liveLearners: data.live_learners,
    referrals: data.referrals,
    newEnrollments: data.today_enrollments,
    newWatchMinutes: data.today_watch_minutes,
    enrollments: data.total_enrollments
  })
});

export const getCourseRatingsInfo = (isTestSeries, collectionUID) => ({
  type: educatorTypes.FETCH_COURSE_RATINGS,
  endpoint: `v1/feedback/metrics/${
    isTestSeries ? 'test_series' : 'plus_course'
  }/${collectionUID}/`,
  method: 'get'
});

export const getLessonMetrics = (lessonUID, lessonType) => ({
  type: educatorTypes.FETCH_LESSON_METRICS,
  endpoint:
    lessonType === 'quiz'
      ? `v1/feedback/metrics/test/${lessonUID}/`
      : `v1/uplus/classes/${lessonUID}/feedback/`,
  method: 'get',
  lessonUID,
  schema: new schema.Object({}),
  processData: (data) => {
    if (lessonType === 'post') {
      const newData = {
        ...data,
        teachingMethod: data.teaching_style,
        courseRelevance: data.topic_relevance,
        classInteraction: data.topic_completeness,
        visualPresentation: data.visual_presentation,
        lessonLikePercent: data.likes_percent,
        questionsAsked: data.questions_asked,
        pollParticipation: data.poll_participation,
        mostUsedEmojis: data.most_used_emojis
      };
      delete newData.teaching_style;
      delete newData.topic_relevance;
      delete newData.topic_completeness;
      delete newData.visual_presentation;
      delete newData.likes_percent;
      delete newData.poll_participation;
      delete newData.questions_asked;
      delete newData.most_used_emojis;
      return newData;
    }
    return data;
  }
});

const getLessonFeedback = (uid, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_EDU_LESSON_FEEDBACK,
  endpoint: next,
  method: 'get',
  key: uid,
  resetWithoutEmpty,
  schema: new schema.Object({}),
  processData: (data) => ({
    ...data,
    results: data.results.map((result) => ({
      ...result,
      text: result.review_text || result.text,
      created_at: result.updated_on || result.created_at
    }))
  })
});

export const fetchLessonFeedback = (
  lessonUID,
  lessonType,
  nextPage,
  resetWithoutEmpty = false
) =>
  paginateAction(
    lessonType === 'quiz'
      ? `v1/feedback/test/${lessonUID}/`
      : `v2/uplus/live-class-review/?live_class_uid=${lessonUID}`,
    getLessonFeedback(lessonUID, resetWithoutEmpty),
    'eduLessonFeedback',
    lessonUID,
    resetWithoutEmpty
  )(nextPage);

export const updateRescheduleFormTextField = (value, id) => ({
  type: educatorTypes.UPDATE_RESCHEDULE_TEXT_FIELD,
  value,
  id
});

export const updateRescheduleDropdownFields = (value, id, multipleValues) => ({
  type: educatorTypes.UPDATE_RESCHEDULE_DROPDOWN_FIELDS,
  value: multipleValues ? value : [value],
  id,
  multipleValues
});

export const getRescheduleInfo = () => ({
  type: educatorTypes.GET_RESCHEDULE_INFO,
  method: 'get',
  endpoint: 'api/policies/class_reschedule',
  hostType: HOST_TYPE.CMS
});

export const rescheduleClass = (data) => ({
  type: educatorTypes.RESCHEDULE_CLASS,
  method: 'post',
  endpoint: 'v1/uplus/moderator/item/reschedule/',
  data
});

export const checkRescheduleRequestWarning = (itemUID) => ({
  type: educatorTypes.RESCHEDULE_REQUEST_WARNING,
  method: 'get',
  endpoint: `v1/uplus/educator/item/post/${itemUID}/reschedule_request_warning/`
});

export const resetRescheduleRequestWarning = () => ({
  type: educatorTypes.RESET_RESCHEDULE_REQUEST_WARNING
});

export const updateDiscussionForumText = (value, id) => ({
  type: educatorTypes.UPDATE_DISCUSSION_FORUM_TEXT,
  value,
  id
});

export const updateDiscussionFormStory = (data, channelId) => ({
  type: educatorTypes.UPDATE_DISCUSSION_FORUM_STORY,
  method: 'post',
  endpoint: `v1/channel/${channelId}/add_story/?response_required=1`,
  data
});

export const setFilterState = (data) => ({
  type: educatorTypes.SET_FILTER,
  data
});

export const fetchGoLiveDetails = (token) => ({
  type: educatorTypes.FETCH_GOLIVE_DETAILS,
  method: 'get',
  endpoint: `v1/uplus/golive/?token=${token}`,
  key: 'goliveDetails'
});

export const getAutoPromotionCampaignInfo = (uid) => ({
  type: educatorTypes.AUTO_PROMOTION_CAMPAIGN_INFO,
  method: 'get',
  endpoint: `v1/group/community/educator/campaign/${uid}/detail/`
});

export const sendAutoPromotionMessage = (uid, offerType) => ({
  type: educatorTypes.SEND_AUTO_PROMOTION_MESSAGE,
  method: 'post',
  endpoint: `v1/group/community/educator/campaign/${uid}/post/`,
  body: { campaign_type: offerType }
});

export const dismissAutoPromotionMessage = (data) => ({
  type: educatorTypes.DISMISS_AUTO_PROMOTION_MESSAGE,
  method: 'post',
  endpoint: 'v1/group/community/screen_visited/',
  data
});

const getTemplatesOfACourse = (key, resetWithoutEmpty) => (next) => ({
  type: educatorTypes.FETCH_TEMPLATES_OF_A_COURSE,
  endpoint: next,
  method: 'get',
  key,
  resetWithoutEmpty,
  schema: paginatorListSchema,
  directPaginatedEntity: true,
  entityType: 'template'
});

export const fetchTemplatesOfACourse = ({
  goalUID = null,
  topicGroupUID = null,
  courseType = null,
  templateState = null,
  templateName = null,
  templateID = null,
  key = undefined,
  isContentOperator = 0,
  nextPage,
  resetWithoutEmpty = false
}) => {
  let urlParam = `${goalUID ? `goal_uid=${goalUID}` : ''}${
    topicGroupUID ? `&topic_group_uid=${topicGroupUID}` : ''
  }${courseType ? `&course_type=${courseType}` : ''}${
    [0, 1, 2].includes(templateState) ? `&state=${templateState}` : ''
  }${templateName ? `&name=${templateName}` : ''}${
    templateID ? `&id=${templateID}` : ''
  }&is_content_operator=${isContentOperator}`;
  if (urlParam[0] === '&') urlParam = urlParam.slice(1);
  const url = `v1/uplus/course_template/list_course_template${
    urlParam ? `?limit=5&${urlParam}` : ''
  }`;
  return paginateAction(
    url,
    getTemplatesOfACourse(key, resetWithoutEmpty),
    'courseTemplates',
    key,
    resetWithoutEmpty
  )(nextPage);
};

export const fetchTemplateDetails = (templateUID) => ({
  type: educatorTypes.FETCH_TEMPLATE_DETAILS,
  endpoint: `v1/uplus/course_template/get_course_template?id=${templateUID}`,
  method: 'get',
  schema: new schema.Object({}),
  processData: (response) => {
    response.quizzes = response.quizzes.map((quiz, index) => ({
      ...quiz,
      index,
      quizUID: quiz.template_quiz?.uid
    }));
    response.live_classes = response.live_classes.map((liveClass, index) => ({
      ...liveClass,
      index,
      learnerNotes: liveClass.learner_notes,
      educatorSlides: liveClass.educator_slides || liveClass.drive_details,
      quizUID: liveClass.practice_quiz?.uid,
      hasPracticeQuiz: !!liveClass.practice_quiz?.uid,
      questions: liveClass.practice_quiz?.max_question_count
    }));
    return Template.getEntityData(response);
  }
});

export const addTemplateLesson = (data) => ({
  type: educatorTypes.ADD_TEMPLATE_LESSON,
  data
});

export const editTemplateCourseLessonWithoutUID = (data) => ({
  type: educatorTypes.EDIT_TEMPLATE_COURSE_LESSON_WITHOUT_UID,
  result: {
    ...data,
    sessions: algo.mergeTwoSortedArray(data.liveClasses, data.quizzes)
  }
});

export const addTemplateCourseLessonWithoutUID = (data) => ({
  type: educatorTypes.ADD_TEMPLATE_COURSE_LESSON_WITHOUT_UID,
  data: {
    ...data,
    sessions: algo.mergeTwoSortedArray(data.liveClasses, data.quizzes)
  }
});

export const setUpTemplateEditSession = (value) => ({
  type: educatorTypes.SETUP_TEMPLATE_EDIT_SESSION,
  value
});

export const saveTemplate = (data) => ({
  type: educatorTypes.SAVE_TEMPLATE,
  endpoint: 'v1/uplus/course_template/create_update_course_template/',
  method: 'post',
  data,
  schema: new schema.Object({}),
  processData: (response) => Template.getEntityData(response)
});

export const deleteTemplateLessons = (data) => ({
  type: educatorTypes.DELETE_TEMPLATE_LESSONS,
  data
});

export const fetchCourseTemplateFilters = () => ({
  type: educatorTypes.FETCH_COURSE_TEMPLATE_FILTERS,
  endpoint: 'v1/uplus/course_template/get_course_template_filter',
  method: 'get',
  schema: new schema.Object({}),
  processData: (data) => {
    const filterGoalList = [];
    const filterTopicGroups = {};
    data.forEach((goalData) => {
      filterGoalList.push({ name: goalData.goal_name, uid: goalData.goal_uid });
      filterTopicGroups[goalData.goal_uid] = goalData.topic_groups;
    });
    return { filterGoalList, filterTopicGroups };
  }
});

export const setUpCreateTemplateCourseData = (templateData) => ({
  type: educatorTypes.SETUP_TEMPLATE_DATA_FOR_COURSE,
  data: templateData
});

export const setUpTemplateDataInEducatorCourse = (templateData, courseData) => {
  const tempCourseData = {
    selectedTopics: courseData?.selectedTopics || [],
    selectedExams: courseData?.selectedExams || [],
    selectedTopicGroups: courseData?.selectedTopicGroups || []
  };
  if (tempCourseData.selectedTopics.length === 0) {
    tempCourseData.selectedTopics = templateData.topics;
    tempCourseData.selectedExams = templateData.selectedExams;
    tempCourseData.selectedTopicGroups = templateData.selectedTopicGroups;
  }
  const { liveClasses = [], quizzes = [] } = templateData;
  const { sessions: courseSessions } = courseData || {};
  const addData = {
    selectedGoal: [templateData.goal],
    selectedTopics: tempCourseData.selectedTopics,
    selectedExams: tempCourseData.selectedExams,
    selectedTopicGroups: tempCourseData.selectedTopicGroups,
    templateUID: templateData.uid,
    selectedClasses:
      courseData?.selectedClasses || templateData.selectedClasses,
    selectedEducators:
      courseData?.selectedEducators || templateData.selectedEducators,
    schedule: {
      startsFrom: '',
      time: '',
      daysOfWeek: [0, 0, 0, 0, 0, 0, 0],
      duration: ''
    }
  };
  const newData = { ...templateData, ...addData };
  if (!courseSessions?.length)
    newData.sessions = algo.mergeTwoSortedArray(liveClasses, quizzes);
  return {
    type: educatorTypes.SETUP_TEMPLATE_DATA_IN_EDU_COURSE,
    result: newData
  };
};

export const addTemplateCourse = (data) => ({
  type: educatorTypes.ADD_TEMPLATE_COURSE,
  endpoint: 'v2/uplus/template/programme/add/',
  method: 'post',
  data
});

export const editTemplateCourse = (data) => ({
  type: educatorTypes.EDIT_TEMPLATE_COURSE,
  endpoint: 'v2/uplus/template/programme/edit/',
  method: 'post',
  data
});

export const updateTemplateCourseTextField = (value, id) => ({
  type: educatorTypes.UPDATE_TEMPLATE_COURSE_FIELDS,
  value,
  id
});

export const updateScheduleWeekDay = (value) => ({
  type: educatorTypes.UPDATE_SCHEDULE_WEEK_DAY,
  value
});

export const fetchTemplateCourseDetails = (templateUID, collectionUID) => ({
  type: educatorTypes.FETCH_TEMPLATE_COURSE_DETAILS,
  endpoint: `v1/uplus/template/programme/details/?template_id=${templateUID}&collection_uid=${collectionUID}`,
  method: 'get',
  schema: new schema.Object({}),
  processData: (data) => {
    const { collection, template } = data;
    const {
      name: title,
      uid: courseUID,
      description,
      programme,
      languages,
      course_type
    } = collection;
    let { topic_groups = [], topics = [], exams = [] } = collection;
    const { authors, goal } = programme;
    const {
      topics: templateTopics = [],
      topic_groups: templateTopicGroups = [],
      exams: templateExams = []
    } = template || {};
    goal.goal_name = goal.name;
    goal.goal_uid = goal.uid;
    if (!topic_groups?.length) {
      // Course is in step 2
      topics = [...topics, ...templateTopics];
      topic_groups = [...topic_groups, ...templateTopicGroups];
      exams = [...exams, ...templateExams];
    }
    const newData = {
      title,
      description,
      selectedLanguages: languages,
      selectedGoal: [goal],
      selectedEducators: authors.map((author) => ({
        ...author.author_details,
        name: `${author.author_details.first_name} ${author.author_details.last_name}`
      })),
      selectedTopics: topics,
      selectedTopicGroups: topic_groups,
      selectedExams: exams,
      courseUID,
      courseType: course_type
    };
    return newData;
  }
});

export const setScheduleOfATemplateCourse = (data) => ({
  type: educatorTypes.SET_SCHEDULE_OF_TEMPLATE_COURSE,
  endpoint: 'v1/uplus/template/programme/add_schedule/',
  method: 'post',
  data
});

export const fillDurationInSchedule = () => ({
  type: educatorTypes.FILL_DURATION_IN_SCHEDULE
});

export const deleteCourseTemplate = (id) => ({
  type: educatorTypes.DELETE_COURSE_TEMPLATE,
  endpoint: 'v1/uplus/course_template/delete_course_template/',
  method: 'post',
  data: { id }
});

export const clearTemplateSessionAfterAdding = () => ({
  type: educatorTypes.CLEAR_TEMPLATE_SESSION
});

export const clearTemplatesState = () => ({
  type: educatorTypes.CLEAR_TEMPLATES_STATE
});

export const setTemplateLessonDuration = (data) => ({
  type: educatorTypes.SET_TEMPLATE_LESSON_DURATION,
  lessonDuration: data
});

export const clearTemplateData = () => ({
  type: educatorTypes.CLEAR_TEMPLATE_DATA
});

export const deleteTemplateLessonsFromCourse = (
  sessions,
  liveClasses,
  quizzes
) => ({
  type: educatorTypes.DELETE_TEMPLATE_LESSONS_FROM_COURSE,
  sessions,
  liveClasses,
  quizzes
});

export const getFeedBackOptionsForCourseTemplate = (uniqueId) => ({
  type: educatorTypes.GET_FEEDBACK_FOR_COURSE_TEMPLATE,
  endpoint: `v1/feedback/educator_course_template/?object_uid=${uniqueId}`,
  method: 'get',
  key: 'feedBackOptionsForATemplateCourse'
});

export const submitFeedBackForCourseTemplate = (data, feedBackUID) => ({
  type: educatorTypes.SUBMIT_FEEDBACK_FOR_COURSE_TEMPLATE,
  endpoint: `v1/feedback/${feedBackUID}/response/`,
  method: 'post',
  data
});

export const setClashSessionsOfACourse = (data) => ({
  type: educatorTypes.SET_CLASH_SESSIONS_OF_A_COURSE,
  data
});

export const setAutoScheduleClashes = (items, clashes, schedule, ISODate) => ({
  type: educatorTypes.SET_AUTO_SCHEDULE_CLASHES,
  items,
  clashes,
  schedule,
  ISODate
});

export const setLessonRef = (result) => ({
  type: educatorTypes.SET_LESSON_REFS,
  result,
  key: 'lessonRefs'
});

export const setDragInfo = (data) => ({
  type: educatorTypes.SET_DRAG_INFO,
  data
});

export const setTemplateReorderedQuizLessons = (data) => ({
  type: educatorTypes.SET_TEMPLATE_REORDERED_QUIZ_LESSONS,
  data
});

export const clearEducatorCourseUID = () => ({
  type: educatorTypes.CLEAR_COURSE_UID,
  key: 'courseUID'
});

export const removeClashSession = () => ({
  type: educatorTypes.REMOVE_CLASH_SESSIONS
});

export const removeSessionEditError = () => ({
  type: educatorTypes.REMOVE_SESSION_EDIT_ERROR
});

export const setThumbnail = (payload) => ({
  method: 'post',
  endpoint: 'v1/uplus/moderator/item/add_thumbnail/',
  data: payload
});

export const fetchOnBoardingPreferences = (goalUID) => ({
  type: educatorTypes.GET_ON_BOARDING_PREFERENCES,
  endpoint: `v1/uplus/template/programme/onboarding_preferences/?goal_uid=${goalUID}`,
  method: 'get',
  goalUID,
  schema: new schema.Object({}),
  processData: (data) => {
    const {
      class_preferences: classPreferences = [],
      exam_preferences: examPreferences = []
    } = data;
    return { classPreferences, examPreferences };
  }
});

export const createLiveSupportTicket = (live_class_url = '') => ({
  type: educatorTypes.CREATE_LIVE_SUPPORT_TICKET,
  endpoint: 'v1/feedback/educator/support/',
  method: 'post',
  data: {
    live_class_url
  }
});

export const fetchLevelsFaqData = () => ({
  type: educatorTypes.GET_FAQ_LEVELS,
  endpoint: 'api/policies/faq',
  method: 'get',
  hostType: HOST_TYPE.CMS
});

export const fetchLevelsIntroData = () => ({
  type: educatorTypes.GET_LEVELS_INTRO,
  endpoint: 'api/policies/introLevels',
  method: 'get',
  hostType: HOST_TYPE.CMS
});

export const fetchLevelConfigDetails = () => ({
  type: educatorTypes.GET_LEVEL_CONFIG,
  endpoint: 'v1/user/browse/educators/get_config_details/',
  method: 'get'
});

export const fetchEducatorMetaInfo = (channelUID) => ({
  type: educatorTypes.GET_META_INFO,
  endpoint: `v1/users/educator/meta_info/?channel_uid=${channelUID}`,
  method: 'get'
});

export const fetchDppReminderCardStatus = () => ({
  type: educatorTypes.GET_DPP_REMINDER_CARD_STATUS,
  endpoint: 'v1/users/educator/dpp_practice_reminder/',
  method: 'get'
});

export const fetchEducatorLevelInfo = (goalUID = '') => {
  const params = goalUID ? `&goal_uid=${goalUID}` : '';
  return {
    type: educatorTypes.GET_LEVEL_INFO,
    endpoint: `v1/users/educator/level-info/?is_promotion_info_required=1${params}`,
    method: 'get'
  };
};

export const updateEducatorLevelInfo = (payload) => ({
  type: educatorTypes.UPDATE_LEVEL_INFO,
  payload
});

export const fetchEducatorLevelStats = (goalUID) => ({
  type: educatorTypes.GET_LEVEL_STATS,
  endpoint: `v1/user/browse/educators/levels-stats/?goal_uid=${goalUID}&include_current_month_stats=1`,
  method: 'get'
});

export const fetchLevelWiseEducators = (
  goalUID,
  levelID,
  offset,
  resetResults
) => ({
  type: educatorTypes.GET_LEVEL_WISE_EDUCATOR,
  endpoint: `v1/users/educator/level-wise-educators/?goal_uid=${goalUID}&level_id=${levelID}&offset=${offset}&limit=10`,
  method: 'get',
  resetResults
});

export const markLevelInfoScreenVisited = (educatorUID) => ({
  type: educatorTypes.MARK_LEVEL_INFO_VISITED,
  endpoint: `v1/users/visit_screen/educator_level_intro_card_screen/${educatorUID}/`,
  method: 'post'
});

export const markLevelPromotionVisited = (goalUID) => ({
  type: educatorTypes.MARK_LEVEL_PROMOTION_VISITED,
  endpoint: 'v1/users/educator/mark-level-promotion-screen-visited/',
  method: 'post',
  data: {
    goal_uid: goalUID
  }
});

export const fetchGoalsForStats = () => ({
  type: educatorTypes.GET_GOALS_FOR_STATS,
  endpoint: 'v1/users/educator/allowed-goals/',
  method: 'get'
});

export const updateGoalForStats = (payload) => ({
  type: educatorTypes.UPDATE_GOALS_FOR_STATS,
  payload
});

export const fetchCommunityDetails = (communityUID) => ({
  type: educatorTypes.FETCH_COMMUNITY_DETAILS,
  method: 'get',
  endpoint: `v1/group/community/${communityUID}/basic-info/`
});

export const fetchCommunityShareCard = () => ({
  type: educatorTypes.FETCH_COMMUNITY_SHARE_CARD_INFO,
  method: 'get',
  endpoint: 'api/policies/communitySharePreview',
  hostType: HOST_TYPE.CMS
});

export const fetchCommunityAwareness = () => ({
  type: educatorTypes.FETCH_COMMUNITY_UPDATE_INTRO,
  method: 'get',
  endpoint: 'api/policies/communityAwareness',
  hostType: HOST_TYPE.CMS
});

export const fetchOpenHouseAwareness = () => ({
  type: educatorTypes.FETCH_OPEN_HOUSE_AWARENESS_INFO,
  method: 'get',
  endpoint: 'api/policies/openHouseAwareness',
  hostType: HOST_TYPE.CMS
});

export const fetchCommunityQuizAwareness = () => ({
  type: educatorTypes.FETCH_COMMUNITY_QUIZ_AWARENESS_INFO,
  method: 'get',
  endpoint: 'api/policies/communityQuizAwareness',
  hostType: HOST_TYPE.CMS
});

export const fetchSchedulerAwarenessCmsInfo = () => ({
  type: educatorTypes.FETCH_EDUCATOR_SCHEDULER_AWARENESS_INFO,
  method: 'get',
  endpoint: 'api/policies/educatorSchedulerAwareness',
  hostType: HOST_TYPE.CMS
});

export const fetchCourseTemplatesAwarenessInfo = () => ({
  type: educatorTypes.FETCH_COURSE_TEMPLATES_AWARENESS_INFO,
  method: 'get',
  endpoint: 'api/policies/educatorCourseTemplatesAwareness',
  hostType: HOST_TYPE.CMS
});

export const fetchRMEditsAwarenessCmsInfo = () => ({
  type: educatorTypes.FETCH_RM_EDITS_AWARENESS_INFO,
  method: 'get',
  endpoint: 'api/policies/rmEditsAwareness',
  hostType: HOST_TYPE.CMS
});

export const setAutoDppKnowMoreCardStatus = (eduUID) => ({
  type: educatorTypes.SET_AUTO_DPP_KNOW_MORE_CARD_STATUS,
  endpoint: `v1/users/visit_screen/dpp_intro_card_screen/${eduUID}/`,
  method: 'post'
});

export const setModalState = (data) => ({
  type: educatorTypes.EDUCATOR_MODAL_TYPE,
  data
});

export const fetchDPPQuizInfo = ({ classUID }) => ({
  type: educatorTypes.FETCH_DPP_QUIZ_INFO,
  endpoint: `v1/quizzes/dpp_practice/${classUID}/details`,
  method: 'get'
});

export const fetchDPPQuestions = ({ quizUID, filterParams }) => ({
  type: educatorTypes.FETCH_DPP_QUESTIONS,
  endpoint: `v1/quizzes/dpp_practice/${quizUID}/questions/`,
  method: 'post',
  data: {
    ...filterParams
  }
});

export const updateQuestionOnShuffle = ({ quizUID, questionData }) => ({
  type: educatorTypes.UPDATE_DPP_QUESTION_ON_SHUFFLE,
  endpoint: `v1/quizzes/dpp_practice/${quizUID}/question_shuffle/`,
  method: 'post',
  data: {
    ...questionData
  },
  questionUID: questionData.question_uid
});

export const updateDPPFilterQuestionNumber = (value = 5) => ({
  type: educatorTypes.UPDATE_DPP_FILTER_QUESTION_NUMBER,
  value
});

export const addDPPSubConcept = (data) => ({
  type: educatorTypes.ADD_DPP_SUBCONCEPT,
  data
});

export const removeDPPSubConcept = (data) => ({
  type: educatorTypes.REMOVE_DPP_SUBCONCEPT,
  data
});

export const addSubConceptByUID = (uid) => ({
  type: educatorTypes.ADD_SUBCONCEPT_BY_UID,
  uid
});

export const removeSubConceptByUID = (uid) => ({
  type: educatorTypes.REMOVE_SUBCONCEPT_BY_UID,
  uid
});

export const addDPPFilterDifficulty = (selectedCode) => ({
  type: educatorTypes.ADD_DPP_FILTER_DIFFICULTY,
  selectedCode
});

export const removeDPPFilterDifficulty = (selectedCode) => ({
  type: educatorTypes.REMOVE_DPP_FILTER_DIFFICULTY,
  selectedCode
});

export const selectAllDifficulties = () => ({
  type: educatorTypes.SELECT_ALL_DIFFICULTIES
});

export const removeAllDifficulties = () => ({
  type: educatorTypes.REMOVE_ALL_DIFFICULTIES
});

export const deleteDPP = (classUID) => ({
  type: educatorTypes.DELETE_PRACTICE,
  endpoint: `v1/quizzes/dpp_practice/${classUID}/delete/`,
  method: 'post'
});

export const resetFilter = (data) => ({
  type: educatorTypes.RESET_FILTER,
  data
});

export const setPracticeCtaOnDelete = (classUID) => ({
  type: educatorTypes.SET_PRACTICE_CTA,
  classUID
});

export const updateDPPQuestionDraft = ({ quizUID, questionOrderList }) => ({
  type: educatorTypes.SAVE_DRAFT_DPP_QUESTION,
  endpoint: `v1/quizzes/dpp_practice/${quizUID}/draft/`,
  method: 'post',
  data: questionOrderList
});

export const setDPPQuestionOrder = (questionOrder) => ({
  type: educatorTypes.UPDATE_DPP_QUESTION_ORDER,
  questionOrder
});

export const resetDPPQuestionOrder = () => ({
  type: educatorTypes.RESET_DPP_QUESTION_ORDER
});

export const publishDPPQuiz = ({ quizUID }) => ({
  type: educatorTypes.PUBLISH_DPP_QUIZ,
  endpoint: `v1/quizzes/dpp_practice/${quizUID}/publish/`,
  method: 'post'
});

export const fetchDppFeedbackOptions = () => ({
  type: educatorTypes.FETCH_DPP_FEEDBACK_OPTIONS,
  endpoint: 'v1/feedback/dpp_practice_experience/',
  method: 'get'
});

export const submitDppPracticeFeedback = (uid, data) => ({
  type: educatorTypes.SUBMIT_DPP_PRACTICE_FEEDBACK,
  endpoint: `v1/feedback/${uid}/response/`,
  method: 'post',
  data
});

export const fetchDPPReportQuestionInfo = () => ({
  type: educatorTypes.FETCH_DPP_REPORT_QUESTION_INFO,
  endpoint: 'v1/feedback/dpp_question/',
  method: 'get'
});

export const submitReportDPPQuestion = ({ reportUID, submissionInfo }) => ({
  type: educatorTypes.SUBMIT_REPORT_DPP_QUESTION,
  endpoint: `v1/feedback/${reportUID}/response/`,
  method: 'post',
  data: {
    ...submissionInfo
  }
});

export const fetchCommunityOnboarding = () => ({
  type: educatorTypes.FETCH_COMMUNITY_ONBOARDING,
  method: 'get',
  endpoint: 'api/policies/communityOnboarding',
  hostType: HOST_TYPE.CMS
});

export const sendMessageToCommunity = (data) => ({
  type: educatorTypes.SEND_MESSAGE_TO_COMMUNITY,
  method: 'post',
  endpoint: 'v1/group/community/educator/welcome_posts/',
  data
});

export const markSchedulerInfoScreenVisited = (screen, educatorUID) => ({
  type: educatorTypes.MARK_SCHEDULER_INFO_VISITED,
  endpoint: `v1/users/visit_screen/${screen}/${educatorUID}/`,
  method: 'post'
});

export const setDPPCoursePageLink = (link) => ({
  type: educatorTypes.SET_COURSE_PAGE_LINK,
  link
});

export const setSubConceptNormalizedData = () => ({
  type: educatorTypes.SET_SUBCONCEPT_NORMALIZED_DATA
});

export const fetchDPPSubConceptCount = (data) => ({
  type: educatorTypes.FETCH_DPP_SUBCONCEPT_COUNT,
  endpoint: 'pariksha/v1/practice/question_count/',
  method: 'post',
  data
});

export const setSelectedDifficultyCount = (selectedList) => ({
  type: educatorTypes.SET_SELECTED_DIFFICULTY_COUNT,
  selectedList
});

export const shareDPPInCommunity = (quizUID) => ({
  type: educatorTypes.SHARE_DPP_IN_COMMUNITY,
  endpoint: `v1/quizzes/dpp_practice/${quizUID}/community_post/`,
  method: 'post'
});

export const setTestType = (option) => ({
  type: educatorTypes.SET_TEST_TYPE,
  option
});

export const setTestFrequency = (option) => ({
  type: educatorTypes.SET_TEST_FREQUENCY,
  option
});

export const setBrochure = (option) => ({
  type: educatorTypes.SET_BROCHURE,
  option
});

export const updateRadioBox = (id, value) => {
  return {
    type: educatorTypes.UPDATE_RADIO_BOX,
    value,
    id
  };
};

export const updateRadioBoxForSession = (id, value) => {
  return {
    type: educatorTypes.UPDATE_RADIO_BOX_SESSION,
    value,
    id
  };
};

export const updateCourseTaPoweredState = () => {
  return {
    type: educatorTypes.UPDATE_TA_POWERED_STATE
  };
};

export const fetchAllOfflineCentreCities = () => {
  return {
    type: educatorTypes.FETCH_ALL_OFFLINE_CITIES,
    endpoint: 'v1/offline_centres/cities/',
    method: 'get',
    schema: new schema.Array(),
    key: 'allLanguages',
    processData: (data) => {
      const newData = data;
      return newData;
    }
  };
};

export const fetchAllRoomsOfAnOfflineCenter = (centreUID) => {
  return {
    type: educatorTypes.FETCH_ALL_ROOMS,
    endpoint: `v1/offline_centres/rooms/?center_uid=${centreUID}`,
    method: 'get',
    schema: new schema.Array(),
    key: 'allRooms',
    centreUID,
    processData: (data) => {
      const newData = data;
      return newData;
    }
  };
};

export const changeRoomOfAnOfflineCourse = (courseUID, value) => {
  return {
    type: educatorTypes.CHANGE_ROOM_OF_A_COURSE,
    value,
    courseUID
  };
};

export const fetchSpecialClassTypes = () => ({
  type: educatorTypes.FETCH_SPECIAL_CLASS_TYPES,
  endpoint: 'v1/uplus/class_types/',
  method: 'get',
  schema: new schema.Array(),
  key: 'allSpecialClassTypes',
  processData: (data) => {
    const newData = data;
    return newData;
  }
});

export const addSubConcept = (value, id) => ({
  type: educatorTypes.UPDATE_SUB_CONCEPTS,
  value,
  id
});

export const removeSubConcept = (value, key) => ({
  type: educatorTypes.REMOVE_SUB_CONCEPTS,
  value,
  key
});
